import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Params, RouterStateSnapshot } from '@angular/router';
import { ExperimentationState } from '@interfaces';
import { InvestmentObjectives, IRAOptions } from '@interfaces/models';
import { EntityState } from '@ngrx/entity';
import * as fromRouter from '@ngrx/router-store';
import { ActionReducerMap, createFeatureSelector, createSelector } from '@ngrx/store';
import * as fromOneTimePasscodeState from '@store/reducers/otp/otp.reducer';

import * as fromTestMonkey from '../../../test-monkey/store/reducers/test-monkey.reducer';
import * as fromAccountTriage from './account-triage/account-triage.reducer';
import * as fromBeneficiaries from './beneficiaries/beneficiaries.reducer';
import { Person } from './beneficiaries/beneficiaries.reducer';
import * as fromClientAccounts from './client-accounts/client-accounts.reducer';
import * as fromContent from './content/content.reducer';
import * as fromCostBasis from './cost-basis/cost-basis.reducer';
import * as fromDecedentInfo from './decedent-info/decedent-info.reducer';
import * as fromDocumentUpload from './document-upload/document-upload.reducer';
import * as fromEstate from './estate/estate.reducer';
import * as fromExperimentation from './experimentation/experimentation.reducer';
import * as fromFeature from './feature/feature.reducer';
import { FeatureToggleState } from './feature/feature.reducer';
import * as fromFraudDetectorSimulator from './fraud-detector-simulator/fraud-detector-simulator.reducer';
import * as fromFMF from './fraud-micro-frontend/fmf.reducer';
import * as fromGifting from './gifting/gifting.reducer';
import * as fromInheritedIra from './inherited-ira/inherited-ira.reducer';
import * as fromInternalTransfer from './internal-transfer/internal-transfer.reducer';
import * as fromLetterUpload from './letter-upload/letter-upload.reducer';
import * as fromMinorTransfer from './minor-transfer/minor-transfer.reducer';
import * as fromMXM from './mxm/mxm.reducer';
import * as fromOpenAccount from './open-account/open-account.reducer';
import * as fromOpenChoice from './open-choice/open-choice.reducer';
import * as fromPersonalData from './personal-data/personal-data.reducer';
import * as fromSelectedClientAccounts from './selected-client-accounts/selected-client-accounts.reducer';
import * as fromSpinner from './spinner/spinner.reducer';
import * as fromSubmit from './submit/submit.reducer';
import * as fromTCFTrust from './tcf-trust/tcf-trust.reducer';
import {
  adaptedStateReducer,
  transactionIdReducer,
} from './transaction-state/transaction-state.reducer';
import * as fromTriage from './triage/triage.reducer';
import * as fromTrust from './trust/trust.reducer';
import * as fromTrustTaxInfo from './trust-tax-info/trust-tax-info.reducer';

export interface RouterStateUrl {
  url: string;
  queryParams: Params;
  params: Params;
}

export interface TransactionState {
  beneficiaries: Person[];
  accountTriage: fromAccountTriage.AccountTriageState;
  selectedClientAccounts: fromSelectedClientAccounts.ISelectedClientAccountsState;
  currentTransactionId: string;
  currentTransactionEditMode: boolean;
  iraOption?: IRAOptions;
  investmentObjectives?: InvestmentObjectives;
}

export interface AdaptedState extends EntityState<TransactionState> {}

export interface State {
  beneficiaries: fromBeneficiaries.BeneficiariesState;
  clientAccounts: fromClientAccounts.IClientAccountsState;
  content: fromContent.ContentState;
  costBasis: fromCostBasis.CostBasisState;
  decedentInfo: fromDecedentInfo.DecedentInfoState;
  documentUpload: fromDocumentUpload.IDocumentUploadState;
  letterUpload: fromLetterUpload.ILetterUploadState;
  estate: fromEstate.EstateState;
  featureToggles: FeatureToggleState;
  fraudDetectorSimulator: fromFraudDetectorSimulator.IFraudDetectorSimulatorState;
  fmf: fromFMF.IFMFState;
  gifting: fromGifting.GiftingState;
  internalTransfer: fromInternalTransfer.InternalTransferState;
  minorTransfer: fromMinorTransfer.MinorTransferState;
  mxm: fromMXM.MXMState;
  openAccount: fromOpenAccount.IOpenAccountState;
  otp: fromOneTimePasscodeState.OneTimePasscodeState;
  personalData: fromPersonalData.PersonalDataState;
  routerReducer: fromRouter.RouterReducerState<RouterStateUrl>;
  selectedClientAccounts: fromSelectedClientAccounts.ISelectedClientAccountsState;
  spinner: fromSpinner.ISpinnerState;
  submit: fromSubmit.ISubmitState;
  testMonkey: fromTestMonkey.TestMonkeyState;
  triage: fromTriage.TriageState;
  trust: fromTrust.TrustState;
  trustTaxInfo: fromTrustTaxInfo.TrustTaxInfoState;
  tcfTrust: fromTCFTrust.TCFTrustState;
  experimentation: ExperimentationState;
  transactions: AdaptedState;
  currentTransactionId: string;
  accountTriage: fromAccountTriage.AccountTriageState;
  inheritedIra: fromInheritedIra.InheritedIraState;
}

export const reducers: ActionReducerMap<any> = {
  beneficiaries: fromBeneficiaries.reducer,
  clientAccounts: fromClientAccounts.reducer,
  content: fromContent.reducer,
  costBasis: fromCostBasis.reducer,
  decedentInfo: fromDecedentInfo.reducer,
  documentUpload: fromDocumentUpload.reducer,
  experimentation: fromExperimentation.reducer,
  letterUpload: fromLetterUpload.reducer,
  estate: fromEstate.reducer,
  featureToggles: fromFeature.reducer,
  gifting: fromGifting.reducer,
  fmf: fromFMF.reducer,
  internalTransfer: fromInternalTransfer.reducer,
  minorTransfer: fromMinorTransfer.reducer,
  mockFraudDetector: fromFraudDetectorSimulator.reducer,
  mxm: fromMXM.reducer,
  otp: fromOneTimePasscodeState.reducer,
  openAccount: fromOpenAccount.reducer,
  openChoice: fromOpenChoice.reducer,
  personalData: fromPersonalData.reducer,
  routerReducer: fromRouter.routerReducer,
  selectedClientAccounts: fromSelectedClientAccounts.reducer,
  spinner: fromSpinner.reducer,
  submit: fromSubmit.reducer,
  testMonkey: fromTestMonkey.reducer,
  triage: fromTriage.reducer,
  trust: fromTrust.reducer,
  trustTaxInfo: fromTrustTaxInfo.reducer,
  tcfTrust: fromTCFTrust.reducer,
  transactions: adaptedStateReducer,
  currentTransactionId: transactionIdReducer,
  accountTriage: fromAccountTriage.reducer,
  inheritedIra: fromInheritedIra.reducer,
};

export const getBeneficiariesState =
  createFeatureSelector<fromBeneficiaries.BeneficiariesState>('beneficiaries');
export const getClientAccountsState =
  createFeatureSelector<fromClientAccounts.IClientAccountsState>('clientAccounts');
//TODO: Remove content NGRX code when all files load content directly
// export const getContentState = createSelector(
//   (state: State) => state.content,
//   (value) => value,
// )
export const getCostBasis = createFeatureSelector<fromCostBasis.CostBasisState>('costBasis');
export const getDecedentInfoState =
  createFeatureSelector<fromDecedentInfo.DecedentInfoState>('decedentInfo');
export const getDocumentUploadState =
  createFeatureSelector<fromDocumentUpload.IDocumentUploadState>('documentUpload');
export const getLetterUploadState =
  createFeatureSelector<fromLetterUpload.ILetterUploadState>('letterUpload');
export const getEstateState = createFeatureSelector<fromEstate.EstateState>('estate');
export const getFeatureToggleState = createSelector(
  (state: State) => state.featureToggles,
  (value) => value,
);
export const getFMFState = createFeatureSelector<fromFMF.IFMFState>('fmf');
export const getFraudDetectorSimulatorState =
  createFeatureSelector<fromFraudDetectorSimulator.IFraudDetectorSimulatorState>(
    'mockFraudDetector',
  );
export const getGiftingState = createFeatureSelector<fromGifting.GiftingState>('gifting');
export const getInternalTransferState =
  createFeatureSelector<fromInternalTransfer.InternalTransferState>('internalTransfer');
export const getMinorTransferState =
  createFeatureSelector<fromMinorTransfer.MinorTransferState>('minorTransfer');
export const getMXMState = createFeatureSelector<fromMXM.MXMState>('mxm');
export const getOpenAccountState =
  createFeatureSelector<fromOpenAccount.IOpenAccountState>('openAccount');
export const getOpenChoiceState =
  createFeatureSelector<fromOpenChoice.OpenChoiceState>('openChoice');
export const getOTPState =
  createFeatureSelector<fromOneTimePasscodeState.OneTimePasscodeState>('otp');
export const getPersonalDataState =
  createFeatureSelector<fromPersonalData.PersonalDataState>('personalData');
export const getRouterState =
  createFeatureSelector<fromRouter.RouterReducerState<RouterStateUrl>>('routerReducer');
export const getSelectedClientAccountsState = createSelector(
  (state: State) => state.selectedClientAccounts,
  (value) => value,
);
export const getSpinnerState = createFeatureSelector<fromSpinner.ISpinnerState>('spinner');
export const getSubmitState = createFeatureSelector<fromSubmit.ISubmitState>('submit');
export const getTestMonkeyState =
  createFeatureSelector<fromTestMonkey.TestMonkeyState>('testMonkey');
export const getTriageState = createFeatureSelector<fromTriage.TriageState>('triage');
export const getTrustState = createFeatureSelector<fromTrust.TrustState>('trust');
export const getTrustTaxInfo =
  createFeatureSelector<fromTrustTaxInfo.TrustTaxInfoState>('trustTaxInfo');
export const getTCFTrustState = createFeatureSelector<fromTCFTrust.TCFTrustState>('tcfTrust');
export const getAccountTriageState =
  createFeatureSelector<fromAccountTriage.AccountTriageState>('accountTriage');
export const getExperimentationState =
  createFeatureSelector<ExperimentationState>('experimentation');
export const getInheritedIraState =
  createFeatureSelector<fromInheritedIra.InheritedIraState>('inheritedIra');

@Injectable()
export class CustomSerializer implements fromRouter.RouterStateSerializer<RouterStateUrl> {
  serialize(routerState: RouterStateSnapshot): RouterStateUrl {
    const { url } = routerState;
    const { queryParams } = routerState.root;
    let state: ActivatedRouteSnapshot = routerState.root;

    while (state.firstChild) {
      state = state.firstChild;
    }

    const { params } = state;

    return { url, queryParams, params };
  }
}
